// Ported from genieos/web/src/components/charts/EChartsMultiLineChart.tsx @ 84923d036
//
// Time axis + gamma theme (djin-cloud#806 Task 4). #806's PR1 echarts 6.1.0 probe
// found no workaround needed here: function-valued yAxis.min/max ARE re-evaluated
// on dataZoom against the currently-visible extent, and markLine renders correctly
// on a time axis — both PASS. Kept unchanged from the genieos source.
/**
 * ECharts-based Multi-Line Chart Component
 *
 * Supports multiple series, up to three Y-axes (left / right / right2),
 * reference lines, an optional zoom slider, and a custom tooltip formatter.
 */

import ReactECharts from 'echarts-for-react';
import { use as echartsUse } from 'echarts/core';
import type { EChartsCoreOption } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, MarkLineComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { GAMMA_COLORS } from '@/theme/gamma-echarts-theme';
import { layoutYAxes, type YAxisId } from './yAxisLayout';

echartsUse([LineChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, MarkLineComponent, CanvasRenderer]);

export type { YAxisId };

/** An axis bound: a fixed number, or a function of the currently-visible extent. */
export type AxisBoundValue =
  | number
  | ((extent: { min: number; max: number }) => number);

export interface MultiLineSeriesConfig {
  dataKey: string;
  name: string;
  color: string;
  yAxisId?: YAxisId;
  unit?: string;
  area?: boolean;
  /** Render the line dashed instead of solid (recharts strokeDasharray parity). */
  dashed?: boolean;
  /** Line width in px (recharts strokeWidth parity). Defaults to 2. */
  lineWidth?: number;
}

export interface ReferenceLineConfig {
  value: number;
  label?: string;
  color?: string;
  yAxisId?: YAxisId;
}

export interface EChartsMultiLineChartProps {
  data: any[];
  series: MultiLineSeriesConfig[];
  xAxisKey?: string;
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  /** Render an ECharts zoom slider + inside-zoom (replaces recharts <Brush>). */
  showZoom?: boolean;
  /** Bridge null/gap samples with a continuous line (recharts connectNulls parity). */
  connectNulls?: boolean;
  /**
   * Zoom window to render, as ECharts percentages. Both must be supplied.
   *
   * `notMerge` makes every option rebuild a full replace, so a dataZoom with no
   * explicit window snaps back to 0-100 and silently discards the operator's
   * zoom whenever any other prop changes. A caller that tracks the window can
   * hand it back here to survive those rebuilds (#2795).
   */
  zoomStart?: number;
  zoomEnd?: number;
  /**
   * Fit the Y-axes tightly to the data range instead of forcing a zero baseline
   * (ECharts `scale: true`). Matches recharts' default `dataMin..dataMax` domain.
   * Needed when the data sits far from zero (e.g. ~800 V bus voltages) so small
   * deviations and reference lines stay legible. Defaults to false (zero
   * baseline) so existing area/line charts are unaffected. */
  scaleYAxis?: boolean;
  /**
   * Per-axis lower bound. A number pins the axis; a FUNCTION is handed straight
   * to ECharts, which calls it with the extent of whatever is currently inside
   * the dataZoom window — that is what makes the axis re-fit live as you zoom
   * (dataZoom defaults to filterMode: 'filter', so the extent shrinks with the
   * window). Evaluating the function here instead would freeze the axis at the
   * full-window range. Overrides scaleYAxis for the axes it names. (#2795)
   */
  yAxisMin?: Partial<Record<YAxisId, AxisBoundValue>>;
  /** Per-axis upper bound. See yAxisMin. (#2795) */
  yAxisMax?: Partial<Record<YAxisId, AxisBoundValue>>;
  /**
   * Spline smoothing. Defaults to true for the existing callers, but a
   * historian should set false: once zoomed into a narrow band the spline
   * overshoots between samples and draws values that were never measured.
   */
  smooth?: boolean;
  animationDuration?: number;
  referenceLines?: ReferenceLineConfig[];
  leftYAxisFormatter?: (value: number) => string;
  rightYAxisFormatter?: (value: number) => string;
  right2YAxisFormatter?: (value: number) => string;
  xAxisFormatter?: (value: any) => string;
  /** Custom ECharts tooltip formatter returning an HTML string; overrides the default. */
  tooltipFormatter?: (params: any) => string;
  /** ECharts event handlers, e.g. { datazoom: fn }. Passed straight through. */
  onEvents?: Record<string, (params: any, instance: any) => void>;
  /**
   * Formats a series' legend label from its `name` (djin-cloud#806 Task 5).
   * MultiAxisChart sets each series' `name` to its stable alias so the
   * tooltip can look a series up by name instead of by index; this recovers
   * a human-readable label for the legend without a second name field.
   */
  legendFormatter?: (name: string) => string;
}

export const EChartsMultiLineChart: React.FC<EChartsMultiLineChartProps> = ({
  data,
  series,
  xAxisKey = 'time',
  height = 300,
  showGrid = true,
  showTooltip = true,
  showLegend = true,
  showZoom = false,
  connectNulls = false,
  zoomStart,
  zoomEnd,
  scaleYAxis = false,
  yAxisMin,
  yAxisMax,
  smooth = true,
  animationDuration = 1000,
  referenceLines = [],
  leftYAxisFormatter = (value) => `${value}`,
  rightYAxisFormatter = (value) => `${value}`,
  right2YAxisFormatter = (value) => `${value}`,
  xAxisFormatter,
  tooltipFormatter,
  onEvents,
  legendFormatter,
}) => {
  // One ECharts axis per OCCUPIED unit group, placed by layoutYAxes: the first
  // on the left edge, the second on the right, a third pushed outward on the
  // right (djin-cloud#829). Emitting a fixed left axis and stacking right +
  // right2 on one edge is what drew a kW + Hz chart's labels on top of each
  // other. An axis no series uses is not emitted at all. ECharts treats such an
  // axis as blank and draws nothing for it, split lines included, and the split
  // lines lived on the left axis. So a chart with no `left` group rendered no
  // grid, whatever showGrid said (measured against echarts 6.1.0).
  const layout = layoutYAxes(series.map((s) => s.yAxisId));
  const indexFor = (id?: YAxisId) =>
    layout.find((p) => p.id === (id ?? 'left'))?.index ?? 0;

  // Build series configuration with explicit type for markLine support
  interface SeriesItem {
    name: string;
    type: 'line';
    yAxisIndex: number;
    data: any[];
    smooth: boolean;
    connectNulls: boolean;
    lineStyle: { color: string; width: number; type: 'solid' | 'dashed' };
    itemStyle: { color: string };
    areaStyle?: { color: { type: 'linear'; x: number; y: number; x2: number; y2: number; colorStops: { offset: number; color: string }[] } };
    symbol: 'none';
    animationDuration: number;
    animationEasing: 'cubicOut';
    markLine?: any;
  }

  const seriesData: SeriesItem[] = series.map((s) => ({
    name: s.name,
    type: 'line' as const,
    yAxisIndex: indexFor(s.yAxisId),
    data: data.map((row) => [row[xAxisKey], row[s.dataKey] ?? null]),
    smooth,
    connectNulls,
    lineStyle: {
      color: s.color,
      width: s.lineWidth ?? 2,
      type: s.dashed ? 'dashed' : 'solid',
    },
    itemStyle: {
      color: s.color,
    },
    areaStyle: s.area ? {
      color: {
        type: 'linear' as const,
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: `${s.color}66` }, // 40% opacity
          { offset: 1, color: `${s.color}0D` }, // 5% opacity
        ],
      },
    } : undefined,
    symbol: 'none' as const,
    animationDuration,
    animationEasing: 'cubicOut' as const,
  }));

  // Attach reference lines as a markLine on the first series of the matching axis.
  const attachRefLines = (id: YAxisId) => {
    const lines = referenceLines.filter((r) => (r.yAxisId ?? 'left') === id);
    if (lines.length === 0) return;
    const target = seriesData.find((s) => s.yAxisIndex === indexFor(id));
    if (!target) return;
    // Group-level style is the fallback; each reference line carries its own
    // color so multiple differently-colored refs can share one axis.
    target.markLine = {
      silent: true,
      // Reference lines are static threshold annotations — they must honor the
      // same animation budget as the data series. Without this the markLine
      // keeps ECharts' default grow-on animation and replays it on every
      // notMerge re-render (e.g. the GFD chart, which passes animationDuration=0
      // but re-renders ~every 2s on the battery poll), sweeping the threshold
      // lines left-to-right each cycle.
      animation: animationDuration > 0,
      lineStyle: {
        // Gamma defines no markLine styling, so there's no theme fallback to
        // lean on here (djin-cloud#806 Ruling V) — this needs a real value.
        // textMuted is a solid, moderate-weight neutral, closer to the
        // original 30%-alpha white than GAMMA_COLORS.border's 12%-alpha
        // emerald, which would render this near-invisible.
        color: GAMMA_COLORS.textMuted,
        type: 'dashed',
      },
      data: lines.map((rl) => ({
        yAxis: rl.value,
        lineStyle: {
          color: rl.color || GAMMA_COLORS.textMuted,
          type: 'dashed',
        },
        label: {
          formatter: rl.label || `${rl.value}`,
          // Inside the plot, above the line. 'end' drew the label just past
          // the grid's right edge, which is a right-hand axis's label column.
          // A nominal is normally a tick value, so "60 Hz" printed straight
          // over the axis's own "60" (djin-cloud#829).
          position: 'insideEndTop',
        },
      })),
    };
  };
  layout.forEach((p) => attachRefLines(p.id));

  // Only emit min/max when a bound was actually supplied for that axis —
  // an explicit `min: undefined` is not the same as omitting the key, and
  // would stop ECharts falling back to its own (or scaleYAxis's) behaviour.
  const boundsFor = (id: YAxisId) => {
    const bounds: { min?: AxisBoundValue; max?: AxisBoundValue } = {};
    if (yAxisMin?.[id] !== undefined) bounds.min = yAxisMin[id];
    if (yAxisMax?.[id] !== undefined) bounds.max = yAxisMax[id];
    return bounds;
  };

  // Formatters belong to the unit group (the logical id), not to the edge the
  // group happens to be drawn on.
  const formatterFor: Record<YAxisId, (value: number) => string> = {
    left: leftYAxisFormatter,
    right: rightYAxisFormatter,
    right2: right2YAxisFormatter,
  };

  const yAxis = layout.map((p) => ({
    type: 'value' as const,
    scale: scaleYAxis,
    ...boundsFor(p.id),
    position: p.side,
    offset: p.offset,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      color: GAMMA_COLORS.textMuted,
      fontSize: 12,
      formatter: formatterFor[p.id],
    },
    // Split lines are drawn at one axis's ticks, so exactly one axis owns them:
    // the first placed axis, which has a series whenever the chart has any
    // (ECharts draws nothing, split lines included, for an axis without one).
    splitLine: p.index === 0 && showGrid ? {
      lineStyle: {
        color: GAMMA_COLORS.gridLine,
        type: 'dashed' as const,
      },
    } : {
      show: false,
    },
  }));

  // Room on the right for what containLabel does not reserve. Measured against
  // echarts 6.1.0, it neither fully contains an offset axis's tick labels nor
  // any crosshair label, and a crosshair label that would leave the container
  // is shifted back INWARD, toward the axis beside it. The values match the
  // previous right / right+right2 margins, now keyed on how many axes share the
  // right edge.
  const rightAxes = layout.filter((p) => p.side === 'right').length;
  const rightGap = rightAxes >= 2 ? 110 : rightAxes === 1 ? 60 : 20;
  // Leave room for the zoom slider at the bottom when enabled.
  const bottomGap = showZoom ? 64 : 40;

  // Half a window would pin one edge and leave the other at its default, which
  // is worse than not restoring at all — so require both.
  const zoomWindow =
    zoomStart !== undefined && zoomEnd !== undefined
      ? { start: zoomStart, end: zoomEnd }
      : {};

  const option: EChartsCoreOption = {
    grid: {
      top: showLegend ? 40 : 20,
      right: rightGap,
      bottom: bottomGap,
      left: 60,
      containLabel: true,
    },
    legend: showLegend ? {
      top: 10,
      // No textStyle here (djin-cloud#806 Ruling V) — the gamma theme already
      // defines legend.textStyle; restating it here would shadow the theme
      // and drift from it the next time either one changes.
      data: series.map((s) => s.name),
      ...(legendFormatter ? { formatter: legendFormatter } : {}),
    } : undefined,
    xAxis: {
      type: 'time',
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: GAMMA_COLORS.textMuted,
        fontSize: 12,
        formatter: xAxisFormatter,
      },
      // The crosshair's X label defaults to the raw category value, which for a
      // time series is an unreadable epoch (#2795) sitting right next to a
      // tooltip that formats the same instant correctly. Scoped to this axis so
      // the Y crosshair keeps ECharts' numeric formatting.
      ...(xAxisFormatter
        ? {
            axisPointer: {
              label: { formatter: (p: { value: any }) => xAxisFormatter(p.value) },
            },
          }
        : {}),
    },
    yAxis,
    dataZoom: showZoom ? [
      { type: 'inside', ...zoomWindow },
      {
        type: 'slider',
        ...zoomWindow,
        height: 24,
        bottom: 8,
        showDetail: false,
        // borderColor/fillerColor/handleStyle/textStyle deliberately omitted
        // (djin-cloud#806 Ruling V) — the gamma theme already defines a full
        // dataZoom block; restating these here would shadow it.
      },
    ] : undefined,
    tooltip: showTooltip ? {
      trigger: 'axis',
      backgroundColor: '#0d1520',
      // borderColor deliberately omitted (djin-cloud#806 Ruling V) — the
      // gamma theme already defines tooltip.borderColor; restating it here
      // would shadow the theme.
      borderWidth: 1,
      textStyle: {
        color: '#fff',
      },
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
        },
      },
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
    } : undefined,
    series: seriesData,
  };

  return (
    <ReactECharts
      option={option}
      theme="gamma"
      style={{ height: `${height}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
      lazyUpdate={true}
      onEvents={onEvents}
    />
  );
};
